import { useChannelMix } from "@/lib/queries";
import { formatINR } from "@/lib/utils";
import { ChartPieSlice, TrendUp } from "@phosphor-icons/react";

const CH_COLORS = {
  airbnb: "#FF5A5F", booking: "#003580", agoda: "#5E2FBE", goibibo: "#F9A825",
  mmt: "#E63946", vrbo: "#0D67B4", expedia: "#FDB913", cleartrip: "#F58220",
  yatra: "#E4002B", easemytrip: "#0EA5E9", direct: "#D4AF37", manual: "#64748B", other: "#94A3B8",
};
const colorFor = (c) => CH_COLORS[c.key] || "#94a3b8";

/**
 * Channel Performance — revenue / ADR / lead-time / length-of-stay per OTA,
 * plus a direct-vs-OTA split. Built entirely from bookings we already ingest
 * (no Aiosell call needed).
 */
export default function ChannelMixCard() {
  const { data, isLoading } = useChannelMix();
  const channels = data?.channels || [];
  if (isLoading) return null;
  if (!channels.length) return null;

  const totalRev = channels.reduce((s, c) => s + (c.revenue || 0), 0) || 1;
  const dvo = data?.direct_vs_ota || {};
  const directRev = dvo.direct?.revenue || 0;
  const otaRev = dvo.ota?.revenue || 0;
  const directPct = Math.round((directRev / (directRev + otaRev || 1)) * 100);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ChartPieSlice size={18} weight="duotone" className="text-amber-500" />
        <h2 className="font-manrope text-sm font-bold text-slate-800 dark:text-white">Channel Performance</h2>
        <span className="ml-auto text-[11px] text-slate-400">{directPct}% direct · {100 - directPct}% OTA</span>
      </div>

      {/* Revenue share bar */}
      <div className="flex h-2 rounded-full overflow-hidden mb-3">
        {channels.map((c) => (
          <div key={c.channel} title={`${c.channel} · ${formatINR(c.revenue)}`}
            style={{ width: `${(c.revenue / totalRev) * 100}%`, background: colorFor(c) }} />
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-400 text-[10px] uppercase tracking-wide">
              <th className="text-left font-semibold py-1.5">Channel</th>
              <th className="text-right font-semibold py-1.5">Bookings</th>
              <th className="text-right font-semibold py-1.5">Revenue</th>
              <th className="text-right font-semibold py-1.5">ADR</th>
              <th className="text-right font-semibold py-1.5">Avg stay</th>
              <th className="text-right font-semibold py-1.5">Lead time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {channels.map((c) => (
              <tr key={c.channel}>
                <td className="py-2">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(c) }} />
                    <span className="font-medium text-slate-700 dark:text-slate-200">{c.channel}</span>
                  </span>
                </td>
                <td className="text-right text-slate-600 dark:text-slate-300">{c.bookings}</td>
                <td className="text-right font-mono font-semibold text-slate-800 dark:text-white">{formatINR(c.revenue)}</td>
                <td className="text-right font-mono text-slate-600 dark:text-slate-300">{formatINR(c.adr)}</td>
                <td className="text-right text-slate-600 dark:text-slate-300">{c.avg_length_of_stay}n</td>
                <td className="text-right text-slate-600 dark:text-slate-300">{c.avg_lead_days != null ? `${c.avg_lead_days}d` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[10px] text-slate-400 flex items-center gap-1">
        <TrendUp size={11} /> Lead time = days between booking and check-in. Higher direct % = lower OTA commission.
      </p>
    </div>
  );
}
